import { useState, useCallback } from "react";

import { css } from "@compiled/react";
import { DEFAULT_VALUES } from "@libraries/web-framework";
import { FormattedMessage, useIntl } from "react-intl";

import { media } from "@atlaskit/primitives/responsive";
import { token } from "@atlaskit/tokens";
import UFOSegment from "@atlassian/wac-ufo-segment";

import { Play } from "@/libraries/wpl/foundations/src/icons/object/play";
import { YoutubeModal } from "@/patterns/modal/src";

const backgroundStyles = css({
    backgroundRepeat: "no-repeat",
    backgroundPosition: "center",
    backgroundSize: "cover",
    padding: "90px 0 140px",
    textAlign: "center",
    [media.only.sm]: {
        minHeight: "480px",
        padding: "90px 0 120px 0",
        marginBlockEnd: "128px"
    },
    [media.above.md]: {
        minHeight: "480px",
        padding: "48px 0 120px 0",
        marginBlockEnd: "128px"
    }
});

// TODO: check this background color
const textContainerStyles = css({
    backgroundColor: "#0065ff" + "cc",
    position: "relative",
    marginInlineStart: token("space.200"),
    marginInlineEnd: token("space.200"),
    maxWidth: "1440px",
    flexDirection: "column",
    alignItems: "center",
    minHeight: 0,
    [media.only.sm]: {
        display: "inline-flex",
        padding: "32px 48px"
    },
    [media.above.md]: {
        display: "inline-flex",
        padding: "48px 22px"
    },
    [media.below.sm]: {
        display: "flex",
        padding: "32px 0"
    },
    h1: {
        fontSize: "2.375rem",
        fontWeight: 700,
        marginBlockEnd: "28px",
        marginBlockStart: token("space.0"),
        lineHeight: "56px",
        color: "#ffffff",
        WebkitFontSmoothing: "antialiased",
        textAlign: "center",
        [media.only.sm]: {
            fontSize: "2.75rem"
        },
        [media.above.md]: {
            fontSize: "3rem"
        }
    },
    h3: {
        fontWeight: 500,
        fontSize: token("space.200"),
        marginBlockStart: token("space.0"),
        marginBlockEnd: "28px",
        color: "#ffffff",
        WebkitFontSmoothing: "antialiased",
        letterSpacing: "0.3px",
        lineHeight: token("space.300"),
        [media.only.sm]: {
            fontSize: token("space.300"),
            marginBlockEnd: token("space.400")
        },
        [media.above.md]: {
            fontSize: token("space.300"),
            lineHeight: "32px",
            marginBlockEnd: token("space.400")
        }
    }
});

const playVideoIconStyles = css({
    display: "flex",
    marginInlineEnd: token("space.200"),
    svg: {
        fill: "#ffffff",
        height: "22.6px",
        width: "22.6px",
        [media.above.sm]: {
            height: "28px",
            width: "28px"
        }
    }
});

const playVideoTextStyles = css({
    fontWeight: "400",
    fontSize: token("space.200"),
    lineHeight: "22.4px",
    [media.above.sm]: {
        fontSize: token("space.250"),
        lineHeight: "28px"
    }
});

export const Hero = () => {
    const intl = useIntl();
    const [isOpen, setIsOpen] = useState(false);
    const openModal = useCallback(() => setIsOpen(true), []);
    const closeModal = useCallback(() => setIsOpen(false), []);

    return (
        <UFOSegment name="hero">
            <div
                css={backgroundStyles}
                style={{
                    backgroundImage:
                        "url(https://wac-cdn.atlassian.com/misc-assets/webp-images/Impossible-Alone-Video-Header-Image-%402X%25202.webp)"
                }}
            >
                <div css={textContainerStyles}>
                    <div
                        css={css({
                            maxWidth: "475px",
                            padding: `0 ${token("space.200")} 0 ${token(
                                "space.200"
                            )}`,
                            [media.above.md]: {
                                maxWidth: "none"
                            }
                        })}
                    >
                        <h1>
                            <FormattedMessage
                                id="HomePage.Hero.title"
                                defaultMessage={"Impossible Alone."}
                                values={DEFAULT_VALUES}
                            />
                        </h1>
                    </div>
                    <div
                        css={css({
                            width: "192px",
                            textAlign: "center",
                            [media.above.sm]: {
                                width: "400px"
                            }
                        })}
                    >
                        <h3>
                            <FormattedMessage
                                id="HomePage.Hero.tagline"
                                defaultMessage={
                                    "See how collaboration makes the impossible, possible."
                                }
                            />
                        </h3>
                    </div>
                    <a
                        href="https://www.youtube.com/watch?v=CTETwaQ1dqI"
                        data-event="clicked"
                        data-uuid="577b23c8-e4"
                        data-event-component="linkButton"
                        data-event-container="videoLink"
                        data-schema-version="1"
                        data-label-english="Play video"
                        onClick={(event) => {
                            event.preventDefault();
                            openModal();
                        }}
                        css={css({
                            alignItems: "center",
                            display: "inline-flex",
                            color: "#ffffff"
                        })}
                    >
                        <div css={playVideoIconStyles}>
                            <Play size="22.6" />
                        </div>
                        <span css={playVideoTextStyles}>
                            <FormattedMessage
                                id="HomePage.Hero.PlayVideo"
                                defaultMessage={"Play video"}
                            />
                        </span>
                    </a>
                </div>
            </div>

            <YoutubeModal
                isOpen={isOpen}
                closeModal={closeModal}
                src="https://www.youtube.com/embed/CTETwaQ1dqI?autoplay=1&amp;rel=0&amp;origin=https%3A%2F%2Fwww.atlassian.com&amp;widget_referrer=https%3A%2F%2Fwww.atlassian.com&amp;enablejsapi=1&amp;widgetid=1&autoplay=1"
                title={intl.formatMessage({
                    id: "HomePage.Hero.ModalYouTubeIframeTitle",
                    defaultMessage:
                        "Impossible Alone. Possible Together | Atlassian"
                })}
                closeIconLabel={intl.formatMessage({
                    id: "HomePage.Hero.CloseModalButtonMobileLabel",
                    defaultMessage: "Close modal"
                })}
            />
        </UFOSegment>
    );
};
